/* eslint-disable consistent-return */
import { logout, getCheckoutFirstRoute } from 'actions/auth';
import * as packsActions from 'actions/packs';
import * as commonActions from 'actions/common';
import logger from 'utils/logger';
import { PLATFORMS } from 'utils/constants';
import { sessionStorage } from 'utils/storage';
import { isKeplerPlatform } from 'utils/helpers';

export function createRedirectLink({ name, params = {}, query = {} }) {
  const target = {
    ...(name ? { name } : {}),
    ...(Object.keys(params).length ? { params } : {}),
    ...(Object.keys(query).length ? { query } : {}),
  };

  return Object.keys(target).length ? JSON.stringify(target) : undefined;
}

export function requireLoginStrict(store, { location }) {
  if (!store.getState().auth.isLoggedIn) {
    return {
      name: 'log-in',
      params: {
        platform: location.params?.platform,
      },
      query: {
        redirectTo: createRedirectLink(location.name === 'home' ? { ...location, name: undefined } : location),
      },
    };
  }
}

export function requireLoginEnabled(store) {
  const state = store.getState();
  if (state.settings.loginEnabled) {
    return;
  }

  // Open service is inactive, show error
  if (!state.country.isOpenServiceActive) {
    throw Error('Faulty configuration. Login disabled and is not an open service');
  }

  return { name: 'home' };
}

export function requireLogin(store, { location }) {
  const state = store.getState();

  if (state.auth.isLoggedIn || state.country.isOpenServiceActive) {
    return;
  }

  // Open service is inactive, redirect to login
  return {
    name: 'log-in',
    query: {
      redirectTo: createRedirectLink(location.name === 'home' ? { ...location, name: undefined } : location),
    },
  };
}

/**
 * Remember last location by saving it in a query parameter `redirectTo`
 */
export function rememberLastLocation(store, { location, previousLocation, previousRoute }) {
  if (previousLocation.query.redirectTo) { // carry on redirect link
    return {
      ...location,
      query: {
        ...location.query,
        redirectTo: previousLocation.query.redirectTo,
      },
    };
  }

  if (
    !previousRoute.couldBeInitialView // do not create redirectTo param for couldBeInitialView route
    || previousRoute.name === 'home' // skip 'home' cause anyway we redirect to 'home' by default
    || location.query?.redirectTo // don't create redirectTo if following location already have it
  ) {
    return;
  }

  return {
    ...location,
    query: {
      ...location.query,
      redirectTo: createRedirectLink(previousLocation),
    },
  };
}

/**
 * Remember promo code by saving it in a query parameter `promoCode`
 */
export function rememberPromoCode(store, { location, previousLocation }) {
  if (!store.getState().settings.features.payment?.allowPromoCodes) {
    return;
  }

  const locationsToIgnore = ['checkout', 'checkout-confirmation', 'checkout-receipt', 'checkout-upgrade'];

  if (locationsToIgnore.includes(previousLocation?.name)) {
    return;
  }

  const promoCode = previousLocation?.query.promoCode;

  if (promoCode) {
    return { // add promoCode query param to the current location
      ...location,
      query: {
        ...location.query,
        promoCode,
      },
    };
  }
}

/**
 * Remember offer ID by saving it in a query parameter `packageId`
 */
export function rememberPackageId(store, { location, previousLocation }) {
  const locationsToIgnore = ['checkout', 'checkout-confirmation', 'checkout-receipt'];

  if (locationsToIgnore.includes(previousLocation?.name)) {
    return;
  }

  const packageId = previousLocation?.query.packageId;

  if (packageId) {
    return {
      ...location,
      query: {
        ...location.query,
        packageId,
      },
    };
  }
}

/**
 * Remember identity by saving it in a query parameter `identity`
 */
export function rememberIdentity(store, { location, previousLocation }) {
  const state = store.getState();

  if (state.auth.isLoggedIn && !state.auth.isAnonymousUser) {
    return;
  }

  const identity = previousLocation?.query.identity;

  if (identity) {
    return { // add identity query param to the current location
      ...location,
      query: {
        ...location.query,
        identity,
      },
    };
  }
}

export async function requirePayment(store, { location }) {
  try {
    const state = store.getState();

    // we don't care about anonymous users here
    if (!state.auth.isLoggedIn) {
      return;
    }

    // check if it is mandatory to have entitlement after registration
    if (!state.settings.features.payment?.onboarding?.mandatory) {
      return;
    }

    // check if user has any entitlements
    if (!store.getState().packs.entitled.length) {
      // check again if user has any entitlements in case fetch entitlement was not called before
      await store.dispatch(packsActions.fetchEntitledPacks());
      if (!store.getState().packs.entitled.length) {
        // go to checkout if no entitlement
        return store.dispatch(getCheckoutFirstRoute(location.query));
      }
    }
  } catch (err) {
    logger.error('REQUIRE_PAYMENT_SUBSCRIPTION_ERROR', err);
    throw err;
  }
}

export function requireLogout(store) {
  const { auth } = store.getState();
  // anonymous user is considered as logout
  if (auth.isLoggedIn && !auth.isAnonymousUser) {
    return { name: 'home' };
  }
}

export function requireCreateAccountEnabled(store) {
  const { country, settings } = store.getState();
  if (!country.isCreateAccountEnabled || !settings.features.register) {
    return { name: 'home' };
  }
}

export async function doLogout(store) {
  try {
    await store.dispatch(logout(__CLIENT__));

    return {
      name: 'log-in',
      reload: true,
    };
  } catch (e) {
    logger.error('LOGOUT_ERROR', e);
    throw e;
  }
}

export function redirectWatchContext(store, { location }) {
  const { id, playableId } = location.params;
  return {
    name: 'watch',
    params: {
      id,
      playableId,
    },
  };
}

export function validatePlatform(_, { location }) {
  const validPlatforms = [
    PLATFORMS.VIZIO,
    PLATFORMS.WEBOS,
    PLATFORMS.TIZEN,
    PLATFORMS.BROWSER,
    PLATFORMS.MOVISTAR,
    PLATFORMS.IOS,
    PLATFORMS.ANDROID,
    PLATFORMS.KEPLER,
  ];
  if (!validPlatforms.includes(location.params?.platform?.toLowerCase())) {
    return { name: 'create-account' };
  }
}

export function requireLoginForAddingToWatchlist(store, { location }) {
  const state = store.getState();

  if (state.auth.isLoggedIn || !('addToWatchlist' in location.query)) {
    return;
  }

  return {
    name: 'log-in',
    query: {
      redirectTo: createRedirectLink(location),
    },
  };
}

export function redirectPlatform(store, { location }) {
  const platform = location.params?.platform?.toLowerCase();
  const { common } = store.getState();
  if (platform && location.query?.redirectTo && !common.platformLogin) {
    store.dispatch(commonActions.updatePlatformInfo({
      ...common,
      platformLogin: {
        location,
      },
    }));
  } else if (!platform && common.platformLogin) {
    return common.platformLogin.location;
  }
}

export function requireNotSignUpViaMovistar(store) {
  if (store.getState().user.isSignUpViaMovistar) {
    return {
      name: 'settings-payment',
    };
  }
}

/**
 * Middleware to prevent anonymous users from accessing certain routes
 * Redirects to login page with preserved URL for purchase flows and device-auth
 * Redirects to home page for other routes
 */
export function requireNonAnonymousUser(store, { location }) {
  if (store.getState().auth.isAnonymousUser) {
    // Routes that should redirect to login with the original URL preserved
    const redirectLoginRoutes = ['device-auth', 'purchase-offer', 'checkout-media'];
    
    const shouldRedirectToLogin = redirectLoginRoutes.includes(location.name);
    
    return shouldRedirectToLogin ? {
      name: 'log-in',
      params: {
        platform: location.params?.platform,
      },
      query: {
        redirectTo: createRedirectLink(location),
      },
    } : { name: 'home' };
  }
}

/**
 * Middleware to prevent users from kepler platform from accessing registration routes
 * Kepler users should only be able to login
 * Checks URL parameter, Redux state, and sessionStorage
 */
export function requireNotKeplerForRegistration(store, { location }) {
  const state = store.getState();
  const platform = location.params?.platform;
  const isKepler = isKeplerPlatform(platform, state.common.isKeplerLogin);
  
  if (isKepler) {
    return {
      name: 'log-in',
      params: {
        platform: location.params?.platform,
      },
      query: {
        redirectTo: createRedirectLink(location),
      },
    };
  }
}

/**
 * Middleware to track kepler platform as a persistent session variable
 * Sets isKeplerLogin flag when user accesses from kepler platform
 * Persists to sessionStorage for session persistence
 */
export function trackKeplerLogin(store, { location }) {
  const platform = location.params?.platform;

  if (platform?.toLowerCase() === PLATFORMS.KEPLER) {
    const { common } = store.getState();
    if (!common.isKeplerLogin) {
      store.dispatch(commonActions.setKeplerLogin(true));
      sessionStorage.setItem('isKeplerLogin', 'true');
    }
  }
}
